import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as yup from 'yup';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  eventDate: yup.string().required('Event date is required'),
  message: yup.string().required('Message is required'),
});

const Contact = () => {
  const [submitStatus, setSubmitStatus] = useState({ success: false, error: null });

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      eventDate: '',
      message: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await fetch('http://localhost:5000/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        });

        if (!response.ok) {
          throw new Error('Failed to send message');
        }

        setSubmitStatus({ success: true, error: null });
        formik.resetForm();
      } catch (error) {
        setSubmitStatus({ success: false, error: error.message });
      }
    },
  });

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Typography variant="h1" align="center" sx={{ mb: 6, color: 'primary.main' }}>
          Book Your Event
        </Typography>

        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h3" sx={{ mb: 2, color: 'primary.main' }}>
                Get in Touch
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Ready to experience authentic whole hog BBQ at your next event? Fill out the form below
                and we'll get back to you within 24 hours.
              </Typography>
              <Typography variant="body1">
                We specialize in:
                <ul>
                  <li>Corporate Events</li>
                  <li>Weddings</li>
                  <li>Family Reunions</li>
                  <li>Private Parties</li>
                </ul>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="phone"
                      label="Phone"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      error={formik.touched.phone && Boolean(formik.errors.phone)}
                      helperText={formik.touched.phone && formik.errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="eventDate"
                      label="Event Date"
                      type="date"
                      value={formik.values.eventDate}
                      onChange={formik.handleChange}
                      error={formik.touched.eventDate && Boolean(formik.errors.eventDate)}
                      helperText={formik.touched.eventDate && formik.errors.eventDate}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="message"
                      label="Message"
                      multiline
                      rows={4}
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      error={formik.touched.message && Boolean(formik.errors.message)}
                      helperText={formik.touched.message && formik.errors.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {submitStatus.success && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Message sent successfully!
                      </Alert>
                    )}
                    {submitStatus.error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {submitStatus.error}
                      </Alert>
                    )}
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Container>
  );
};

export default Contact;
